/**
 * WorkPath Hooks (#712)
 *
 * Provides hooks for WorkPath template management and booking
 * WorkPath operations with TanStack Query.
 *
 * WorkPaths are the unified successor to separate inspection/checklist
 * templates: a WorkPath describes an ordered set of tasks, each of which
 * may carry verification rows (checks the mechanic must confirm).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';

// ============================================================================
// TYPES
// ============================================================================

export type WorkPathVerificationType =
  | 'checkbox'
  | 'text'
  | 'photo'
  | 'numeric'
  // #2378 — a rating rather than a tick, so a work path can carry what an
  // inspection carries and feed the customer's service report.
  | 'condition';

/** Stored in the entry's `value`; matches the inspection scale. */
export type WorkPathCondition = 'good' | 'fair' | 'poor' | 'critical';

/** Its own axis, not derived from the condition (#2378). */
export type WorkPathSeverity = 'info' | 'warning' | 'critical';

export type WorkPathStatus = 'pending' | 'in_progress' | 'completed';

export interface WorkPathVerification {
  id: string;
  label: string;
  type: WorkPathVerificationType;
  required: boolean;
  sortOrder: number;
  unit?: string | null;
  /**
   * Whether this line reaches the customer's service report (#2388). On a
   * template `null` means "inherit the template default"; the booking snapshot
   * resolves it to a boolean at instantiation.
   */
  showInReport?: boolean | null;
  // Booking work paths echo the saved entry value + completion time so the UI
  // hydrates persisted fill state after reload instead of a session-local map (#826).
  value?: string | null;
  completedAt?: string | null;
}

export interface WorkPathTask {
  id: string;
  name: string;
  description?: string | null;
  required: boolean;
  sortOrder: number;
  // Booking work paths carry the rolled-up task status (#826). Templates omit it.
  status?: string;
  // Booking work paths only (#2386). Absent before, which is why the mechanic
  // panel could not show who a task belonged to.
  assignedMechanicId?: string | null;
  supervisorApproved?: boolean;
  supervisorApprovedAt?: string | null;
  verifications: WorkPathVerification[];
}

export interface WorkPathTemplate {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  isDefault: boolean;
  serviceIds: string[] | null;
  tasks: WorkPathTask[];
  taskCount: number;
  sortOrder: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BookingWorkPath {
  id: string;
  bookingId: string;
  templateId: string | null;
  name: string;
  status: WorkPathStatus;
  tasks: WorkPathTask[];
  totalTasks: number;
  completedTasks: number;
  completedAt: string | null;
  completedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * A filled booking work path entry row, as returned by the single-entry
 * update endpoint (`PATCH /work-paths/:id/entries/:entryId`).
 */
export interface BookingWorkPathEntry {
  id: string;
  taskId: string;
  subTaskId: string | null;
  verificationId: string;
  verificationLabel: string;
  verificationType: WorkPathVerificationType;
  showInReport: boolean;
  value: string | null;
  severity: WorkPathSeverity | null;
  completedAt: string | null;
  completedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Payload accepted by create/update template mutations. */
/**
 * A task or verification as *sent*, which is not the same shape as one read
 * back: a row the user just added has no id yet, and the server assigns one.
 * Typing the payload with the read shape forced the editor to send its
 * client-side React key as `id`, which the API rejects as a non-uuid.
 */
export type WorkPathVerificationPayload = Omit<WorkPathVerification, 'id' | 'unit'> & {
  id?: string;
  config?: { unit?: string } | null;
};

export type WorkPathTaskPayload = Omit<WorkPathTask, 'id' | 'verifications'> & {
  id?: string;
  verifications: WorkPathVerificationPayload[];
};

export type WorkPathTemplateInput = {
  name: string;
  description?: string | null;
  isDefault?: boolean;
  serviceIds?: string[] | null;
  tasks: WorkPathTaskPayload[];
  sortOrder?: number;
};

// ============================================================================
// TEMPLATE HOOKS
// ============================================================================

interface TemplateFilters {
  [key: string]: unknown;
  includeInactive?: boolean;
}

export function useWorkPathTemplates(filters?: TemplateFilters) {
  return useQuery({
    queryKey: queryKeys.workPathTemplates.list(filters ?? {}),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.includeInactive) params.set('includeInactive', 'true');
      const qs = params.toString();
      const result = await api.get<{ data: WorkPathTemplate[] }>(
        `/work-path-templates${qs ? `?${qs}` : ''}`,
      );
      return result.data;
    },
  });
}

export function useWorkPathTemplate(id: string | null) {
  return useQuery({
    queryKey: queryKeys.workPathTemplates.detail(id ?? ''),
    queryFn: () => api.get<WorkPathTemplate>(`/work-path-templates/${id}`),
    enabled: !!id,
  });
}

export function useCreateWorkPathTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: WorkPathTemplateInput) =>
      api.post<WorkPathTemplate>('/work-path-templates', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workPathTemplates.all,
      });
    },
  });
}

export function useUpdateWorkPathTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Partial<WorkPathTemplateInput>) =>
      api.patch<WorkPathTemplate>(`/work-path-templates/${id}`, data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workPathTemplates.all,
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workPathTemplates.detail(variables.id),
      });
    },
  });
}

/** Soft-deletes (deactivates) a WorkPath template. */
export function useDeleteWorkPathTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/work-path-templates/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workPathTemplates.all,
      });
    },
  });
}

// ============================================================================
// BOOKING WORKPATH HOOKS
// ============================================================================

export function useBookingWorkPaths(bookingId: string | null) {
  return useQuery({
    queryKey: queryKeys.workPaths.byBooking(bookingId ?? ''),
    queryFn: async () => {
      const result = await api.get<{ data: BookingWorkPath[] }>(
        `/work-paths/by-booking/${bookingId}`,
      );
      return result.data;
    },
    enabled: !!bookingId,
  });
}

export function useBookingWorkPath(id: string | null) {
  return useQuery({
    queryKey: queryKeys.workPaths.detail(id ?? ''),
    queryFn: () => api.get<BookingWorkPath>(`/work-paths/${id}`),
    enabled: !!id,
  });
}

export function useCreateBookingWorkPath() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { bookingId: string; templateId?: string; name?: string }) =>
      api.post<BookingWorkPath>('/work-paths', data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workPaths.byBooking(variables.bookingId),
      });
    },
  });
}

/**
 * Let the booking's own details pick the template (#2476).
 *
 * The server has done this since #2382 but nothing called it, so a workshop
 * that had already told a template which services it covers still picked it
 * by hand on every work order.
 *
 * The response is not the checklist shape. `booking_work_paths` is unique per
 * booking, so this returns one path plus `created`, and answers three
 * different situations: a path was created, one already existed and was
 * returned untouched, or no template matched (`data: null`). Callers need to
 * tell those apart — "nothing matched" is not a failure.
 */
export interface AutoCreateWorkPathResult {
  created: boolean;
  /** Null when no template matched the booking. */
  data?: BookingWorkPath | null;
}

export function useAutoCreateWorkPath() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (bookingId: string) =>
      api.post<AutoCreateWorkPathResult>(`/work-paths/auto/${bookingId}`),
    onSuccess: (_data, bookingId) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workPaths.byBooking(bookingId),
      });
    },
  });
}

export function useDeleteBookingWorkPath() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; bookingId: string }) =>
      api.delete(`/work-paths/${id}`),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workPaths.byBooking(variables.bookingId),
      });
    },
  });
}

/** Payload accepted by the single-entry update endpoint. */
export interface UpdateWorkPathEntryInput {
  /** Work path id (path segment). */
  id: string;
  /** Entry id (path segment). */
  entryId: string;
  /** Booking id — used only to invalidate the by-booking query. */
  bookingId: string;
  /**
   * New stored value: 'true'/'false' for checkbox, free text, the number as a
   * string for numeric, the uploaded photo URL for photo entries, or one of
   * the four ratings for condition entries. `null` clears the value.
   */
  value?: string | null;
  showInReport?: boolean;
  /**
   * Condition entries only — the API rejects it on any other type. Omit to
   * leave the stored severity alone; `null` clears it.
   */
  severity?: WorkPathSeverity | null;
}

/**
 * Requests a presigned upload URL for a work path photo entry.
 *
 * Reuses the existing checklist photo upload-url mechanism rather than
 * introducing a new endpoint: the route only uses the id/itemId to build a
 * file name and returns a presigned storage URL. The caller PUTs the file to
 * `uploadUrl`, then persists the returned `fileUrl` as the entry value via
 * {@link useUpdateWorkPathEntry}.
 */
export function useWorkPathPhotoUploadUrl() {
  return useMutation({
    mutationFn: ({
      workPathId,
      entryId,
      mimeType,
      fileExtension,
    }: {
      workPathId: string;
      entryId: string;
      mimeType: string;
      fileExtension?: string;
    }) =>
      api.post<{ uploadUrl: string; fileKey: string; fileUrl: string }>(
        `/checklists/${workPathId}/photos/upload-url`,
        { itemId: entryId, mimeType, fileExtension },
      ),
  });
}

/**
 * Updates (fills) a single booking work path entry via
 * `PATCH /work-paths/:id/entries/:entryId`. Invalidates both the work path
 * detail query and the by-booking list so progress counters refresh.
 */
export function useUpdateWorkPathEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, entryId, value, showInReport, severity }: UpdateWorkPathEntryInput) =>
      api.patch<BookingWorkPathEntry>(`/work-paths/${id}/entries/${entryId}`, {
        ...(value !== undefined ? { value } : {}),
        ...(showInReport !== undefined ? { showInReport } : {}),
        ...(severity !== undefined ? { severity } : {}),
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workPaths.detail(variables.id),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workPaths.byBooking(variables.bookingId),
      });
    },
  });
}

interface WorkPathTaskMutationInput {
  /** Work path id (path segment). */
  id: string;
  /** Task id (path segment). */
  taskId: string;
  /** Booking id — used only to invalidate the by-booking query. */
  bookingId: string;
}

/**
 * Assigns a work path task to a mechanic, or clears the assignment with `null`
 * (#2386). Task-level rather than path-level: a quality checklist is one
 * person's responsibility even when the rest of the path is someone else's.
 */
export function useAssignWorkPathTaskMechanic() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      taskId,
      mechanicId,
    }: WorkPathTaskMutationInput & { mechanicId: string | null }) =>
      api.patch(`/work-paths/${id}/tasks/${taskId}/mechanic`, { mechanicId }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.workPaths.detail(variables.id) });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workPaths.byBooking(variables.bookingId),
      });
    },
  });
}

/**
 * Supervisor sign-off for a single task (#2386). Requires `inspections.approve`
 * — a mechanic's `checklists.manage` is deliberately not enough, since that
 * would let them approve their own work. Pass `approved: false` to reopen a
 * task approved by mistake.
 */
export function useApproveWorkPathTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, taskId, approved = true }: WorkPathTaskMutationInput & { approved?: boolean }) =>
      api.post(`/work-paths/${id}/tasks/${taskId}/approve`, { approved }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.workPaths.detail(variables.id) });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workPaths.byBooking(variables.bookingId),
      });
    },
  });
}

/**
 * Final sign-off for the whole work path (#2386) — the handover check that the
 * car is ready to release, separate from per-task approval.
 */
export function useFinalApproveWorkPath() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      approved = true,
    }: Omit<WorkPathTaskMutationInput, 'taskId'> & { approved?: boolean }) =>
      api.post<BookingWorkPath>(`/work-paths/${id}/final-approve`, { approved }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.workPaths.detail(variables.id) });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workPaths.byBooking(variables.bookingId),
      });
    },
  });
}
